import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import {
  TYPE_LABELS, TYPE_DEFAULT_COLOR, COLOR_CLASSES, COLOR_PALETTE,
} from "@/lib/exam-tile-tokens";

/**
 * Shared assessment node Add/Edit modal.
 * Used by Exam Structure (Sprint 19a) and Curriculum (Sprint 19b).
 *
 * Props:
 *   node   — the node object (or null/new shape with isNew=true)
 *   onClose() — close handler
 *   onSave(updatedNode) — receives the cleaned node payload
 *   testidPrefix — string used to namespace data-testid attributes
 */
export function AssessmentNodeDialog({ node, onClose, onSave, testidPrefix = "es" }) {
  const [d, setD] = useState({
    ...node,
    color: node.color || TYPE_DEFAULT_COLOR[node.type] || "slate",
  });

  const submit = (e) => {
    e?.preventDefault?.();
    if (!d.name?.trim()) { toast.error("Assessment name required"); return; }
    onSave({
      ...d,
      name: d.name.trim(),
      max_marks: d.max_marks ? parseFloat(d.max_marks) : null,
      weight_pct: d.weight_pct ? parseFloat(d.weight_pct) : null,
      color: d.color || null,
    });
  };

  const preview = COLOR_CLASSES[d.color] || COLOR_CLASSES.slate;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-card rounded-md border border-border max-w-md w-full p-5 space-y-3"
        data-testid={`${testidPrefix}-node-dialog`}
      >
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">{d.isNew ? "Add" : "Edit"} assessment</h3>
          <button type="button" onClick={onClose}><X className="h-4 w-4" /></button>
        </div>

        <div>
          <Label className="text-[11px] uppercase tracking-wider font-semibold mb-1.5 block">Type</Label>
          <Select
            value={d.type}
            onValueChange={(v) => setD({ ...d, type: v, color: d.color || TYPE_DEFAULT_COLOR[v] || "slate" })}
          >
            <SelectTrigger data-testid={`${testidPrefix}-node-type`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-[11px] uppercase tracking-wider font-semibold mb-1.5 block">Name</Label>
          <Input value={d.name || ""} onChange={(e) => setD({ ...d, name: e.target.value })} data-testid={`${testidPrefix}-node-name`} />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold mb-1.5 block">Max marks</Label>
            <Input type="number" value={d.max_marks || ""} onChange={(e) => setD({ ...d, max_marks: e.target.value })} data-testid={`${testidPrefix}-node-marks`} />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold mb-1.5 block">Weight %</Label>
            <Input type="number" value={d.weight_pct || ""} onChange={(e) => setD({ ...d, weight_pct: e.target.value })} data-testid={`${testidPrefix}-node-weight`} />
          </div>
        </div>

        <div>
          <Label className="text-[11px] uppercase tracking-wider font-semibold mb-1.5 block">Scheduled month</Label>
          <Input value={d.scheduled_month || ""} onChange={(e) => setD({ ...d, scheduled_month: e.target.value })} placeholder="e.g. May" data-testid={`${testidPrefix}-node-month`} />
        </div>

        <div>
          <Label className="text-[11px] uppercase tracking-wider font-semibold mb-1.5 block">Colour</Label>
          <div className="flex items-center gap-1.5 flex-wrap" data-testid={`${testidPrefix}-node-color-palette`}>
            {COLOR_PALETTE.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setD({ ...d, color: p.key })}
                className={`h-7 w-7 rounded-full ${p.swatch} flex items-center justify-center ring-offset-2 transition ${d.color === p.key ? "ring-2 ring-foreground/70" : "hover:ring-2 hover:ring-muted-foreground/40"}`}
                title={p.label}
                aria-label={`Set colour ${p.label}`}
                data-testid={`${testidPrefix}-node-color-${p.key}`}
              >
                {d.color === p.key && <Check className="h-3.5 w-3.5 text-white drop-shadow" />}
              </button>
            ))}
          </div>
          <div
            className={`mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-md border-2 ${preview.bg} ${preview.border} ${preview.text}`}
            data-testid={`${testidPrefix}-node-color-preview`}
          >
            <span className={`${preview.pill} h-2.5 w-2.5 rounded-full`}></span>
            <span className="text-[11px] font-semibold">Preview · {TYPE_LABELS[d.type]}</span>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" data-testid={`${testidPrefix}-node-save`}>{d.isNew ? "Add" : "Update"}</Button>
        </div>
      </form>
    </div>
  );
}
